import React, { useState } from 'react';
import {
  Grid,
  Button,
  Collapse,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import FilterAutocompleteAgency from '~/components/FilterAutocompleteAgency';
import FilterAutocompleteDispatchsGroups from '~/components/FilterAutocompleteDispatchsGroups';
import FilterAutoCompleteTypesWithCategory from '~/components/FilterAutoCompleteTypesWithCategory';
import FilterAutocompleteSubtypesByTypes from '~/components/FilterAutocompleteSubtypesByTypes';
import FilterAutocompleteArea from '~/components/FilterAutocompleteArea';
import FilterAutocompleteClosedStatus from '~/components/FilterAutocompleteClosedStatus';
import FilterAutocompletePeriod from '~/components/FilterAutocompletePeriod';
import { IStatisticsFilters } from '~/interfaces/IStatistics';
import { useLang } from '~/hooks/useLang';

interface DashboardFiltersProps {
  readonly onFiltersChange: (filters: IStatisticsFilters) => void;
  readonly initialFilters?: Partial<IStatisticsFilters>;
  readonly hidden?: boolean;
  readonly clearFilters: () => void;
}

export default function DashboardFilters({ onFiltersChange, initialFilters, hidden = true, clearFilters }: DashboardFiltersProps) {
  const { translate } = useLang();
  const [agencyId, setAgencyId] = useState<number | null>(initialFilters?.agencyId || null);
  const [dispatchGroupIds, setDispatchGroupIds] = useState<readonly number[]>(initialFilters?.dispatchGroupIds || []);
  const [typeIds, setTypeIds] = useState<readonly number[]>(initialFilters?.typeIds || []);
  const [subtypeIds, setSubtypeIds] = useState<readonly number[]>(initialFilters?.subtypeIds || []);
  const [areaId, setAreaId] = useState<number | null>(initialFilters?.areaId || null);
  const [closedStatusId, setClosedStatusId] = useState<number | null>(initialFilters?.closedStatusId || null);
  const [period, setPeriod] = useState<string>('0');
  const [startDate, setStartDate] = useState<Dayjs | null>(
    initialFilters?.startDate ? dayjs(initialFilters.startDate) : dayjs().subtract(24, 'hour')
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    initialFilters?.finishDate ? dayjs(initialFilters.finishDate) : dayjs()
  );

  const handleClearFilters = () => {
    setAgencyId(null);
    setDispatchGroupIds([]);
    setTypeIds([]);
    setSubtypeIds([]);
    setAreaId(null);
    setClosedStatusId(null);
    setPeriod('0');
    setStartDate(dayjs().subtract(24, 'hour'));
    setEndDate(dayjs());
    clearFilters();
  };

  const handleApplyFilters = () => {
    if (!startDate) {
      return;
    }

    const filters: IStatisticsFilters = {
      startDate: startDate.format('YYYY-MM-DD'),
      ...(agencyId && { agencyId: agencyId }),
      ...(dispatchGroupIds.length > 0 && { dispatchGroupIds: dispatchGroupIds }),
      ...(typeIds.length > 0 && { typeIds: typeIds }),
      ...(subtypeIds.length > 0 && { subtypeIds: subtypeIds }),
      ...(areaId && { areaId: areaId }),
      ...(closedStatusId && { closedStatusId: closedStatusId }),
      ...(endDate && { finishDate: endDate.format('YYYY-MM-DD') }),
    };

    onFiltersChange(filters);
  };

  return (
    <Collapse in={hidden}>
      <Grid
        container
        xs={12}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          paddingX: 2,
          paddingBottom: 2,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'pt-br'}>
          <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid item xs={12} sm={6} md={4}>
            <FilterAutocompleteAgency
              agencyId={agencyId}
              onAgencyChange={(id) => setAgencyId(id)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FilterAutocompleteArea
              areaId={areaId}
              onAreaChange={(id) => setAreaId(id)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FilterAutocompleteClosedStatus
              closedStatusId={closedStatusId}
              onClosedStatusIdChange={(id) => setClosedStatusId(id)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FilterAutocompleteDispatchsGroups
              dispatcheGroups={dispatchGroupIds}
              onGroupDispatchChange={(ids) => setDispatchGroupIds(ids)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FilterAutoCompleteTypesWithCategory
              typeId={typeIds}
              subtypes={subtypeIds}
              categoryId={null}
              onTypeChange={(ids) => setTypeIds(ids)}
              onSubtypesChange={(ids) => setSubtypeIds(ids)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FilterAutocompleteSubtypesByTypes
              typeId={[...typeIds]}
              subtypes={subtypeIds}
              onSubtypesChange={(ids) => setSubtypeIds(ids)}
            />
          </Grid>

            <FilterAutocompletePeriod
              period={period}
              onPeriodChange={(value) => setPeriod(value)}
              onStartDateChange={(value) => setStartDate(value)}
              onFinishDateChange={(value) => setEndDate(value)}
              startDateValue={startDate ? startDate.format('YYYY-MM-DD') : null}
              endDateValue={endDate ? endDate.format('YYYY-MM-DD') : null}
              xsPeriod={4}
              xsDateBetween={4}
              requiredStartDate={true}
            />

          <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2} mt={2}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleClearFilters}
              sx={{ height: '40px', minWidth: '150px' }}
            >
              {translate('Limpar Filtros')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyFilters}
              disabled={!startDate}
              sx={{ height: '40px', minWidth: '150px' }}
            >
              {translate('Aplicar Filtros')}
            </Button>
          </Grid>
          </Grid>
        </LocalizationProvider>
      </Grid>
    </Collapse>
  );
}
